import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

// Колко от видимата ширина прелиства стрелката.
const ARROW_STEP_RATIO = 0.7;
// Под толкова пиксела до ръба се брои „стигнат" (субпикселно закръгляне).
const EDGE_TOLERANCE = 2;
// Колко въздух остава около активния таб, когато се доскролва до него.
const ACTIVE_MARGIN = 24;

// Преливането по краищата е маска (прозрачност), не цвят — важи и за двете
// теми без .dark вариант.
const FADE_CLASSES = {
  none: "",
  start: "[mask-image:linear-gradient(to_right,transparent,black_40px)]",
  end: "[mask-image:linear-gradient(to_left,transparent,black_40px)]",
  both: "[mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)]",
} as const;

const ARROW_BUTTON =
  "absolute top-1/2 z-[1] flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-shade-3 bg-surface text-[11px] text-body shadow-[0_2px_8px_rgba(28,25,23,0.14)] transition-[opacity,color] duration-150 hover:text-ink [@media(pointer:coarse)]:hidden";

/**
 * Хоризонтален ред с табове, който скролва, когато не се събира: без видим
 * scrollbar, с преливане по ръба, от който има още съдържание, стрелки за
 * мишка (на тъч — плъзгане), колелцето на мишката скролва настрани, а
 * активният таб (`data-active="true"` на детето) се доскролва във видимото
 * при смяна на `activeKey` и когато порасне. Общ за табовете на регионите в
 * „Резервации" и в редактора на схемата, и за лентата с обекти на схемата
 * (`activeKey` null).
 *
 * Скролиращият ред има 4px въздух от всички страни (`p-1 -m-1`): overflow-x
 * реже и по вертикала, а така сянката на вдигнатия таб и focus ring-ът не се
 * изрязват, без да се мени мястото, което редът заема. `role` (напр.
 * "tablist") отива на самия ред — стрелките остават извън него.
 */
const ScrollTabs = ({
  activeKey,
  className = "",
  role,
  children,
}: {
  // Смяна → активният таб се доскролва във видимото.
  activeKey: string | number | null;
  // Класовете на реда с табовете (gap, padding).
  className?: string;
  // ARIA ролята на реда с табовете (без стрелките).
  role?: "tablist";
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const start = scroller.scrollLeft > EDGE_TOLERANCE;
    const end = maxScroll - scroller.scrollLeft > EDGE_TOLERANCE;
    setEdges((previous) => (previous.start === start && previous.end === end ? previous : { start, end }));
  }, []);

  // Размерът на реда и на табовете (броячите се сменят) → преизмерване.
  // Наблюдателите се създават веднъж (не при всеки render на родителя —
  // „Резервации" се рендерира на всеки тик); добавен/махнат таб се хваща от
  // MutationObserver-а, който пренасочва ResizeObserver-а към новите деца.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    measure();
    const resizeObserver = new ResizeObserver(measure);
    const observeTabs = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(scroller);
      Array.from(scroller.children).forEach((child) => resizeObserver.observe(child));
    };
    observeTabs();
    const mutationObserver = new MutationObserver(() => {
      observeTabs();
      measure();
    });
    mutationObserver.observe(scroller, { childList: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [measure]);

  // Вертикалното колелце скролва настрани — само когато има накъде, иначе
  // страницата си скролва нормално. Нативен слушател: React-овият е пасивен.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const canScroll =
        event.deltaY > 0 ? scroller.scrollLeft < maxScroll - EDGE_TOLERANCE : scroller.scrollLeft > EDGE_TOLERANCE;
      if (!canScroll) return;
      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    };
    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, []);

  // Активният таб — във видимото. Ръчно scrollTo (не scrollIntoView), за да
  // не мърда и вертикалния скрол на страницата.
  const revealActive = useCallback(() => {
    const scroller = scrollerRef.current;
    const active = scroller?.querySelector<HTMLElement>('[data-active="true"]');
    if (!scroller || !active) return;
    // Редът е `relative` → offsetLeft е в координатите на съдържанието му
    // (не зависи от текущото превъртане).
    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    const viewStart = scroller.scrollLeft;
    const viewEnd = viewStart + scroller.clientWidth;
    if (left - ACTIVE_MARGIN < viewStart) {
      scroller.scrollTo({ left: Math.max(0, left - ACTIVE_MARGIN), behavior: "smooth" });
    } else if (right + ACTIVE_MARGIN > viewEnd) {
      scroller.scrollTo({ left: right + ACTIVE_MARGIN - scroller.clientWidth, behavior: "smooth" });
    }
  }, []);

  // При смяна на активния таб — и докато е активен, при всяка промяна на
  // широчината му (броячът „3 маси · 22 места" порасна, преименуване):
  // иначе порасналият край остава под стрелката.
  useEffect(() => {
    revealActive();
    const active = scrollerRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    let lastWidth = active.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (active.offsetWidth === lastWidth) return;
      lastWidth = active.offsetWidth;
      revealActive();
    });
    observer.observe(active);
    return () => observer.disconnect();
  }, [activeKey, revealActive]);

  const scrollByStep = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: direction * scroller.clientWidth * ARROW_STEP_RATIO, behavior: "smooth" });
  };

  const fade = edges.start && edges.end ? "both" : edges.start ? "start" : edges.end ? "end" : "none";

  return (
    <div className="relative h-full min-w-0">
      <div
        ref={scrollerRef}
        role={role}
        onScroll={measure}
        className={`relative -m-1 flex min-w-0 items-center overflow-x-auto p-1 overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${FADE_CLASSES[fade]} ${className}`}
      >
        {children}
      </div>
      {edges.start && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("common.scrollLeft")}
          onClick={() => scrollByStep(-1)}
          className={`${ARROW_BUTTON} left-0.5`}
        >
          <LeftOutlined />
        </button>
      )}
      {edges.end && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("common.scrollRight")}
          onClick={() => scrollByStep(1)}
          className={`${ARROW_BUTTON} right-0.5`}
        >
          <RightOutlined />
        </button>
      )}
    </div>
  );
};

export default ScrollTabs;
